/**
 * BRAND RATING DISPLAY
 * 
 * Shows aggregate ratings for a brand in a visual bar format
 * Each category shows as a progress bar with score
 */

import { AppIcons } from '../../constants/icons';
import type { BrandRatings } from '../../types/brand';

interface BrandRatingDisplayProps {
  ratings: BrandRatings;
  compact?: boolean;  // Smaller version for cards
}

// Rating category labels and order
const RATING_CATEGORIES = [
  { key: 'formulaQuality', label: 'Formula Quality', description: 'Application, consistency' },
  { key: 'colorAccuracy', label: 'Color Accuracy', description: 'Matches photos' },
  { key: 'longevity', label: 'Longevity', description: 'Wear time' },
  { key: 'customerService', label: 'Customer Service', description: 'Responsiveness' },
  { key: 'shippingSpeed', label: 'Shipping Speed', description: 'Delivery time' },
  { key: 'packaging', label: 'Packaging', description: 'Presentation & safety' },
  { key: 'value', label: 'Value', description: 'Price vs quality' },
  { key: 'brushQuality', label: 'Brush Quality', description: 'Shape & bristles' },
] as const;

export default function BrandRatingDisplay({ ratings, compact = false }: BrandRatingDisplayProps) {
  // Get color based on rating
  const getRatingColor = (rating: number): string => {
    if (rating >= 4.5) return 'bg-green-500';
    if (rating >= 4.0) return 'bg-emerald-500';
    if (rating >= 3.5) return 'bg-lime-500';
    if (rating >= 3.0) return 'bg-yellow-500';
    if (rating >= 2.5) return 'bg-orange-500';
    return 'bg-red-500';
  };

  // Format rating to 1 decimal
  const formatRating = (rating: number): string => {
    return rating.toFixed(1);
  };

  if (compact) {
    // Compact version - just show overall rating
    const overall = ratings.overall;
    if (!overall) return null;

    return (
      <div className="flex items-center gap-1">
        <AppIcons.Wishlist className="w-4 h-4 text-yellow-500 fill-yellow-500" />
        <span className="font-medium text-glass">{formatRating(overall.average)}</span>
        <span className="text-glass-muted text-sm">({overall.count})</span>
      </div>
    );
  }

  // Full display
  return (
    <div className="space-y-3">
      {/* Overall Rating */}
      {ratings.overall && (
        <div className="flex items-center gap-4 p-4 bg-slate-600/50 rounded-lg mb-4">
          <div className="flex items-center gap-2">
            <AppIcons.Wishlist className="w-8 h-8 text-yellow-500 fill-yellow-500" />
            <span className="text-3xl font-bold text-glass">
              {formatRating(ratings.overall.average)}
            </span>
          </div>
          <div className="text-sm text-glass-secondary">
            <div className="font-medium">Overall Rating</div>
            <div>Based on {ratings.overall.count} {ratings.overall.count === 1 ? 'rating' : 'ratings'}</div>
          </div>
        </div>
      )}

      {/* Individual Categories */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {RATING_CATEGORIES.map(({ key, label }) => {
          const rating = ratings[key as keyof BrandRatings];
          if (!rating || typeof rating === 'number') return null;

          return (
            <div key={key} className="flex items-center gap-3">
              <div className="w-32 text-sm text-glass-secondary truncate" title={label}>
                {label}
              </div>
              <div className="flex-grow h-2 bg-slate-600 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${getRatingColor(rating.average)}`}
                  style={{ width: `${(rating.average / 5) * 100}%` }}
                />
              </div>
              <div className="w-12 text-right">
                <span className="font-medium text-glass">{formatRating(rating.average)}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 pt-2 text-xs text-glass-muted">
        <span className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          Poor
        </span>
        <span className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
          Average
        </span>
        <span className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
          Excellent
        </span>
      </div>
    </div>
  );
}
